/**
 * Money conversion helpers for integer USD cents.
 *
 * Every currency amount in the router is stored and transported as integer
 * cents (see ADR-003 / platform-api migration 0008). These helpers bridge
 * the integer representation with the decimal-string rendering that
 * tenant-facing JSON bodies expose for display.
 *
 * Decimal-string output (not decimal-number) is deliberate: a JSON number
 * `5.00` loses its trailing zero on parse and round-trips as `5`, which
 * breaks downstream currency formatting. Strings preserve the exact shape.
 *
 * Kept as pure functions — no Intl, no locale — so Workers runtimes,
 * Node tests, and Vite-bundled consoles all produce the same output.
 */

/**
 * Convert an integer cents value to a decimal-string with exactly two
 * fractional digits. Negative values are prefixed with `-`. `NaN` /
 * non-finite inputs throw — callers must sanitize upstream (this function
 * should never see bogus data from a validated D1 INTEGER column).
 *
 * Examples:
 *   centsToUsdString(500)   → "5.00"
 *   centsToUsdString(-125)  → "-1.25"
 *   centsToUsdString(0)     → "0.00"
 *   centsToUsdString(5)     → "0.05"
 */
export function centsToUsdString(cents: number): string {
  if (!Number.isFinite(cents)) {
    throw new Error(`centsToUsdString: non-finite input ${cents}`);
  }
  const rounded = Math.trunc(cents);
  const sign = rounded < 0 ? '-' : '';
  const abs = Math.abs(rounded);
  const dollars = Math.floor(abs / 100);
  const fractional = abs % 100;
  const paddedFrac = fractional < 10 ? `0${fractional}` : `${fractional}`;
  return `${sign}${dollars}.${paddedFrac}`;
}

/**
 * Parse a decimal-string or decimal-number into integer cents. Accepts
 * "5", "5.0", "5.00", "-1.25", 5, 5.5. Returns `null` on anything that
 * can't be interpreted cleanly (empty string, NaN, Infinity, non-numeric
 * text). Caller decides how to surface the error.
 *
 * Rounds half-away-from-zero to mirror most human expectations — matches
 * the SQLite `ROUND()` convention used in the 0008 backfill.
 */
export function parseUsdToCents(raw: unknown): number | null {
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return null;
    return roundHalfAway(raw * 100);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return null;
    return roundHalfAway(parsed * 100);
  }
  return null;
}

function roundHalfAway(n: number): number {
  if (n >= 0) return Math.floor(n + 0.5);
  return -Math.floor(-n + 0.5);
}
